import useSetupProgress from '@forwardflow/hooks/customHooks/onboarding/useSetupProgress'
import { useOrganizationUsers } from '@forwardflow/hooks/customHooks/organization'
import { useOrganizationUsersFilter } from '@forwardflow/store/filter'
import { Button } from '@forwardflow/ui'
import { useState } from 'react'
import { Select, message } from 'antd'
import React from 'react'
import OrganizationService from '@forwardflow/services/OrganizationService'
import useGetUserRole from '@forwardflow/hooks/customHooks/useGetUserRole'

// Utility to get user from localStorage
const getUserFromLocalStorage = () => {
   try {
      const userStr = localStorage.getItem('user')
      if (!userStr) return null
      return JSON.parse(userStr)
   } catch {
      return null
   }
}

const FubInboxIntegration = () => {
   const [selectedAgent, setSelectedAgent] = useState('')
   const { data: progressData, mutate: mutateProgress } = useSetupProgress()
   const { page: initialPage, limit } = useOrganizationUsersFilter()
   const [page, setPage] = useState(initialPage)
   const [allAgents, setAllAgents] = useState<any[]>([])
   const {
      data: agents,
      isLoading,
      mutate,
   } = useOrganizationUsers({
      page,
      limit,
      invitationStatus: 'accepted',
   })
   const { data: userRoleData } = useGetUserRole()
   // Get user details from localStorage
   const localUser = getUserFromLocalStorage()
   const userEmail = localUser?.email

   // Consider operator@forward-flow.com as owner
   const isOwner =
      userRoleData?.roles?.includes('Owner') ||
      userEmail === 'operator@forward-flow.com'
   const isOrgInstalled = !!progressData?.fubInboxAppData

   console.log('Local User Email:', userEmail)
   // Append new agents to allAgents when data changes
   React.useEffect(() => {
      if (agents?.data) {
         setAllAgents((prev) =>
            page === 1 ? agents.data : [...prev, ...agents.data]
         )
      }
   }, [agents, page])

   // Find the selected agent object
   const selectedAgentObj = allAgents.find(
      (agent) => agent?.email === selectedAgent
   )

   // Find agent by crmUserId if not owner
   const currentAgentObj = allAgents.find(
      (agent) => String(agent?.email) === String(userEmail)
   )

   const handleOrgInstallUninstall = async () => {
      try {
         if (isOrgInstalled) {
            await OrganizationService.uninstallInboxApp(0)
            message.success('Uninstalled for organization successfully')
         } else {
            await OrganizationService.installInboxApp(0)
            message.success('Installed for organization successfully')
         }
         mutateProgress()
      } catch (error) {
         message.error('Organization operation failed')
      }
   }

   const handleAgentInstallUninstall = async () => {
      console.log('Selected Agent Obj:', selectedAgentObj)
      if (!selectedAgentObj?.crmUserId) return
      try {
         if (selectedAgentObj.fubInboxAppData) {
            await OrganizationService.uninstallInboxApp(
               selectedAgentObj.crmUserId
            )
            message.success('Uninstalled successfully')
         } else {
            await OrganizationService.installInboxApp(
               selectedAgentObj.crmUserId
            )
            message.success('Installed successfully')
         }
         mutate()
      } catch (error) {
         message.error('Operation failed')
      }
   }

   const handleCurrentAgentInstallUninstall = async () => {
      const crmUserId = currentAgentObj?.crmUserId
      if (!crmUserId) return
      try {
         if (currentAgentObj?.fubInboxAppData) {
            await OrganizationService.uninstallInboxApp(crmUserId)
            message.success('Uninstalled successfully')
         } else {
            await OrganizationService.installInboxApp(crmUserId)
            message.success('Installed successfully')
         }
         mutate()
      } catch (error) {
         message.error('Operation failed')
      }
   }

   // Lazy loading on dropdown scroll
   const handlePopupScroll = (e: React.UIEvent<HTMLDivElement, UIEvent>) => {
      const target = e.target as HTMLDivElement
      if (
         !isLoading &&
         agents?.data?.length === limit && // Only fetch if there might be more
         target.scrollTop + target.offsetHeight >= target.scrollHeight - 50 // Near bottom
      ) {
         setPage((prev = 1) => prev + 1)
      }
   }

   return (
      <div className="flex flex-col items-center min-h-screen pt-6">
         <div className="w-full max-w-xl">
            {isOwner ? (
               // Owner UI
               <div className="flex flex-col gap-4 p-6 bg-[#828282]/10 rounded-lg shadow-lg max-w-96">
                  <h2 className="mb-2 font-semibold text-white">
                     Connect Fub Inbox App
                  </h2>
                  <Button
                     className="!rounded bg-primary/10 hover:bg-primary/15 border-primary px-7 !mt-0"
                     onClick={handleOrgInstallUninstall}
                  >
                     {isOrgInstalled
                        ? 'Uninstall for whole organization'
                        : 'Install for whole organization'}
                  </Button>
                  <div className="flex flex-col gap-2">
                     <label className="mb-1 font-medium text-white/80">
                        For selected agent
                     </label>
                     <Select
                        className="w-full !rounded-md"
                        value={selectedAgent || undefined}
                        onChange={(value) => setSelectedAgent(value)}
                        placeholder="Select agent"
                        optionFilterProp="children"
                        showSearch
                        dropdownStyle={{ minWidth: '200px' }}
                        popupClassName="text-black"
                        onPopupScroll={handlePopupScroll}
                        loading={isLoading}
                     >
                        {allAgents.map((agent) => (
                           <Select.Option
                              key={agent?.email}
                              value={agent?.email}
                           >
                              {agent.firstName + ' ' + agent.lastName} (
                              {agent.email})
                           </Select.Option>
                        ))}
                     </Select>

                     <Button
                        className="!rounded bg-primary/10 hover:bg-primary/15 border-primary px-7 !mt-0"
                        onClick={handleAgentInstallUninstall}
                        isDisabled={!selectedAgentObj?.crmUserId}
                     >
                        {selectedAgentObj?.fubInboxAppData
                           ? 'Uninstall'
                           : 'Install'}
                     </Button>
                  </div>
               </div>
            ) : (
               // Non-owner UI
               <div className="flex flex-col gap-4 p-6 bg-[#828282]/10 rounded-lg shadow-lg max-w-96">
                  <h2 className="mb-2 font-semibold text-white">
                     Connect Fub Inbox App
                  </h2>
                  <Button
                     className="!rounded bg-primary/10 hover:bg-primary/15 border-primary px-7 !mt-0"
                     onClick={handleCurrentAgentInstallUninstall}
                     isDisabled={!currentAgentObj?.crmUserId}
                  >
                     {currentAgentObj?.fubInboxAppData
                        ? 'Uninstall'
                        : 'Install'}
                  </Button>
               </div>
            )}
         </div>
      </div>
   )
}

export default FubInboxIntegration
